import { DateTimeRenderer } from '@/components/UI/DateRenderers/DateRenderers';
import Tooltip from '@/components/UI/Tooltip';
import { _isBlank } from '@/littledash';
import cn from 'classnames';
import type { FC } from 'react';
import type { DataTableCellIndexCoordinate } from '@/components/UI/DataTableV2/DataTable.model';
import { DateUtils } from '@/utils/Date.utils';
import styles from '../DataTableV2.module.scss';
import { PickerDisplayFormat, usePickerCellBehavior } from '../hooks/usePickerCellBehavior';
import { useAutoInsertOnActivate } from '../hooks/useAutoInsertOnActivate';

interface SampleCollectedAtCellProps {
  coordinate: DataTableCellIndexCoordinate;
  positionOffset: { x: number; y: number };
}

export const SampleCollectedAtCell: FC<SampleCollectedAtCellProps> = ({ coordinate, positionOffset }) => {
  const { cell, picker, mouseHandlers } = usePickerCellBehavior({
    coordinate,
    positionOffset,
    displayFormat: PickerDisplayFormat.DateTimeHoursMinutesSeconds,
  });

  // Auto-insert current datetime when cell activated on empty cell
  useAutoInsertOnActivate({
    coordinate,
    getValue: () => DateUtils.dateTimeNow(),
  });

  const showLoading = cell.state === 'Loading';
  const isEmpty = _isBlank(cell.value);

  return (
    <div
      className={cn(styles.CellContainer, { [styles.groupBorder]: cell.hasGroupBorder })}
      style={picker.positionStyle}
      onMouseDown={mouseHandlers.onMouseDown}
      onMouseEnter={mouseHandlers.onMouseEnter}
      onMouseUp={mouseHandlers.onMouseUp}
      onDoubleClick={picker.activate}
    >
      {showLoading ? (
        <div className={cn(styles.Cell, styles.Loading)} />
      ) : (
        <Tooltip render={cell.tooltip}>
          <div
            className={cn(
              styles.Cell,
              styles[cell.selectionState],
              { [styles.Active]: picker.active },
              styles[cell.state]
            )}
            data-test-component="SampleCollectedAtCell"
            data-test-element="collect-cell"
            data-test-key={`(${coordinate.column},${coordinate.row})`}
          >
            {isEmpty ? (
              <span className="link blue">+ Collect</span>
            ) : (
              <DateTimeRenderer value={cell.value ?? undefined} defaultResponse={cell.value ?? undefined} />
            )}
            <input
              className={styles.TimestampInput}
              style={{ visibility: picker.active ? 'visible' : 'hidden' }}
              ref={picker.inputRef}
              type="datetime-local"
              onChange={picker.handleInputChange}
            />
          </div>
        </Tooltip>
      )}
    </div>
  );
};
